/*
 * HARTLINO brand layer (behaviour)
 * ------------------------------------------------------------
 * Explicit size choice on product pages. Prestige preselects the first available
 * variant; for apparel that invites wrong-size orders. On a product page opened
 * without a ?variant= link, the size option starts empty and is required. The
 * theme's own product form already runs checkValidity()/reportValidity() before
 * adding to cart, so a required, unchecked size blocks the add; we replace the
 * browser bubble with an inline, announced message.
 *
 * Size options are flagged server-side in snippets/variant-picker.liquid
 * (fieldset[data-size-option]), using the theme's translated size labels.
 */

const pendingFormIds = new Set();
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const sizeFieldsetsFor = (formId) => document.querySelectorAll(`variant-picker[form-id="${CSS.escape(formId)}"] fieldset[data-size-option]`);

const scopeFor = (element) => element.closest('.shopify-section') || document.body;

function applyPendingState(formId) {
  sizeFieldsetsFor(formId).forEach((fieldset) => {
    fieldset.querySelectorAll('input[data-option-position]').forEach((input) => {
      input.checked = false;
      input.required = true;
    });

    // Dropdown style shows the selected value in its toggle button
    const selectedValue = fieldset.querySelector('[id$="-selected-value"]');

    if (selectedValue) {
      selectedValue.textContent = fieldset.dataset.selectSizeLabel;
    }

    scopeFor(fieldset).setAttribute('data-size-pending', '');
  });
}

function clearPendingState(formId, fieldset) {
  pendingFormIds.delete(formId);
  fieldset.querySelector('.size-required-message')?.remove();
  fieldset.removeAttribute('aria-invalid');
  scopeFor(fieldset).removeAttribute('data-size-pending');
}

let messageFrame = null;

function showSizeRequired(fieldset) {
  // "invalid" fires once per radio in the group; handle the group once per frame
  if (messageFrame) {
    return;
  }

  messageFrame = requestAnimationFrame(() => {
    messageFrame = null;

    let message = fieldset.querySelector('.size-required-message');

    if (!message) {
      message = document.createElement('p');
      message.className = 'size-required-message';
      message.setAttribute('role', 'alert');
      fieldset.append(message);
    }

    message.textContent = fieldset.dataset.sizeRequiredMessage;
    fieldset.setAttribute('aria-invalid', 'true');
    fieldset.scrollIntoView({ block: 'center', behavior: prefersReducedMotion.matches ? 'auto' : 'smooth' });
    focusTargetFor(fieldset)?.focus({ preventScroll: true });
  });
}

// Dropdown style keeps its radios inside a closed popover, so focus its toggle button;
// otherwise focus the first size that is not sold out.
function focusTargetFor(fieldset) {
  const dropdownToggle = fieldset.querySelector('button.select');

  if (dropdownToggle) {
    return dropdownToggle;
  }

  const inputs = Array.from(fieldset.querySelectorAll('input[data-option-position]'));
  return inputs.find((input) => !input.nextElementSibling?.classList.contains('is-disabled')) || inputs[0];
}

function init() {
  if (new URL(window.location.href).searchParams.has('variant')) {
    return; // Arriving from a variant link (cart, share, ad) is an explicit choice
  }

  document.querySelectorAll('variant-picker[context="main_product"]').forEach((picker) => {
    const formId = picker.getAttribute('form-id');

    if (formId && picker.querySelector('fieldset[data-size-option]')) {
      pendingFormIds.add(formId);
      applyPendingState(formId);
    }
  });
}

// Capture phase: the theme's own change handler (delegated on body) re-renders the
// product info from preloaded HTML within the same event, so the choice must be
// recorded before it runs, or the re-render below would restore the empty state.
document.addEventListener('change', (event) => {
  const fieldset = event.target.closest?.('fieldset[data-size-option]');

  if (fieldset && event.target.matches('input[data-option-position]')) {
    clearPendingState(event.target.getAttribute('form'), fieldset);
  }
}, true);

document.addEventListener('invalid', (event) => {
  const fieldset = event.target.closest?.('fieldset[data-size-option]');

  if (fieldset) {
    event.preventDefault();
    showSizeRequired(fieldset);
  }
}, true);

// The theme re-renders the product info when another option changes, and the server
// fills in a size again; restore the empty state until the customer picks one.
// "product:rerender" does not bubble, so listen in the capture phase and act once the
// theme has swapped the markup.
document.addEventListener('product:rerender', (event) => {
  const formId = event.target.getAttribute?.('id');

  if (pendingFormIds.has(formId)) {
    requestAnimationFrame(() => {
      if (pendingFormIds.has(formId)) {
        applyPendingState(formId);
      }
    });
  }
}, true);

init();

/*
 * Sticky Add to Cart bar
 * ------------------------------------------------------------
 * Prestige shows product-sticky-bar only once the product form has scrolled
 * above the viewport. On desktop the product-info column is "safe-sticky" and
 * can park the form just under the fixed header: hidden from view, yet still
 * counted as visible, so no purchase action was reachable. Also show the bar
 * while the header covers the form. This only widens the theme's condition
 * (same footer rule), so its own `is-visible` toggling never conflicts.
 */
function headerBottom() {
  const header = document.querySelector('.shopify-section--header');

  if (!header || header.querySelector('.header.is-hidden')) {
    return 0;
  }

  return Math.max(0, header.getBoundingClientRect().bottom);
}

function updateStickyBars() {
  const coveredTo = headerBottom();
  const footer = document.querySelector('.shopify-section--footer');
  const footerInView = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;

  // Queried each time: the theme replaces the bar when the product re-renders
  document.querySelectorAll('product-sticky-bar').forEach((bar) => {
    const form = document.forms[bar.getAttribute('form')];

    if (form) {
      // A form with no height has no buy buttons on the page (purchasing switched off in the template)
      const rect = form.getBoundingClientRect();
      bar.classList.toggle('is-covered-visible', !footerInView && rect.height > 0 && rect.bottom <= coveredTo);
    }
  });
}

let stickyFrame = null;

function scheduleStickyBarUpdate() {
  if (!stickyFrame) {
    stickyFrame = requestAnimationFrame(() => {
      stickyFrame = null;
      updateStickyBars();
    });
  }
}

// The safe-sticky column settles a frame or more after a scroll jump (Page Down,
// End, anchor links) without firing another scroll event, so also watch the forms
// cross the header line, whatever moved them.
let formObserver = null;

function observeForms() {
  formObserver?.disconnect();
  formObserver = new IntersectionObserver(scheduleStickyBarUpdate, { rootMargin: `-${Math.round(headerBottom())}px 0px 0px 0px` });

  document.querySelectorAll('product-sticky-bar').forEach((bar) => {
    const form = document.forms[bar.getAttribute('form')];

    if (form) {
      formObserver.observe(form);
    }
  });
}

if (document.querySelector('product-sticky-bar')) {
  window.addEventListener('scroll', scheduleStickyBarUpdate, { passive: true });
  window.addEventListener('resize', () => {
    observeForms();
    scheduleStickyBarUpdate();
  });
  document.addEventListener('product:rerender', () => requestAnimationFrame(() => {
    observeForms();
    updateStickyBars();
  }), true);
  observeForms();
  updateStickyBars();
}
